<template>
  <page-stepped
    :title="pageTitle"
    :tag="pageTag"
    :rules="rules"
    :save-endpoint="{}"
    :model="model"
    :steps="steps"
    :fetch-endpoint="fetchEndpoint"
    :hide-next-button="true"
    :breadcrumbs="breadcrumbs"
    :show-save-button="false"
    :is-edit="!isCreationMode"
    :readonly="isReadOnly"
    :hash="true"
    @check="checkIsButtonDisabled"
    @checkChanges="checkChanges"
  >
    <template #pre-actions>
      <div class="action-buttons">
        <el-button
          type="primary"
          plain
          icon="el-icon-back"
          @click="backToCampaignsList"
          data-qa="back-to-campaigns-btn"
        >
          {{ $t('Gamification_BackToAllCampaigns') }}
        </el-button>
        <el-button
          v-if="isCreationMode || hasChanges"
          type="primary"
          :disabled="isSaveButtonDisabled"
          @click="toggleSaveFlag"
          data-qa="save-draft-btn"
        >
          {{
            isCreationMode
              ? $t('Gamification_CampaignSaveAsDraft')
              : $t('Gamification_SaveChanges')
          }}
        </el-button>
      </div>
    </template>

    <template #sub-header>
      <div class="game-campaign-sub-header" v-if="!isCreationMode">
        <div class="game-url" v-if="getFrontendUrl">
          <a
            class="game-url__link"
            :href="getFrontendUrl"
            target="_blank"
            rel="noopener noreferrer"
            :title="getFrontendUrl"
          >
            {{ getFrontendUrl }}
          </a>
          <el-tooltip placement="top" :content="$t('Gamification_CopyLink')">
            <el-button
              class="game-url__copy-btn"
              type="text"
              icon="el-icon-copy-document"
              :aria-label="$t('Gamification_CopyLink')"
              :title="$t('Gamification_CopyLink')"
              @click="copyGameUrl"
              data-qa="copy-game-url-btn"
            />
          </el-tooltip>
        </div>
      </div>
    </template>
  </page-stepped>
</template>

<script>
import PageStepped from '@/common/components/page/PageStepped.vue'
import { customConfirm, projectEndpoint, projectRoute } from '@/core/helpers'
import iframeLeaveConfirmationMixin from '@/common/mixins/iframeLeaveConfirmationMixin'
import GameStepSetup from '@/modules/gamification/pages/GameStepSetup.vue'
import GameStepPrizes from '@/modules/gamification/pages/GameStepPrizes.vue'
import GameStepPreview from '@/modules/gamification/pages/GameStepPreview.vue'
import {
  GameCreationTabs,
  GameCreationTabSubStepsMap,
  BracketPredictorGameSpecificSteps,
  GameCreationSubStepId,
  ManualBracketPredictorGameSpecificSteps
} from '@/modules/gamification/enum/GameCreationStepsEnum.js'
import GameStepGameRules from '@/modules/gamification/pages/GameStepGameRules.vue'
import GameStepContentAssets from '@/modules/gamification/pages/GameStepContentAssets.vue'
import GameStepAnalytics from '@/modules/gamification/pages/GameStepAnalytics.vue'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'

export default {
  name: 'GameCampaignPage',
  components: { PageStepped },
  mixins: [iframeLeaveConfirmationMixin],
  data() {
    return {
      model: {
        diff: []
      },
      game: {},
      isSaveButtonDisabled: false,
      isBracketPredictorGame: true,
      campaign: {},
      completedSubSteps: [],
      currentSubStep: undefined,
      lastFinishedStep: undefined,
      hasChanges: false,
      saveFlag: false,
      gameCampaignId: null
    }
  },
  beforeRouteLeave(to, from, next) {
    // todo: needs to be reviewed for a better fix when user tries to save draft and then the leave dialog appears
    if (this.confirmedViaPostMessage) {
      this.confirmedViaPostMessage = false
      return next()
    }
    if (!this.hasChanges || (this.saveFlag === true && this.isCreationMode)) {
      return next()
    }

    this.discardChangesConfirm()
      .then(() => {
        next()
      })
      .catch(() => {
        next(false)
      })
  },
  computed: {
    steps() {
      const steps = [
        {
          name: GameCreationTabs.SET_UP,
          title: this.$t('CampaignStep_Setup'),
          component: GameStepSetup,
          completed: this.checkIsTabCompleted(GameCreationTabs.SET_UP),
          bindProps: {
            isBracketPredictorGame: this.isBracketPredictorGame,
            gameCampaignId: this.getGameCampaignId,
            isReadOnly: this.isReadOnly,
            isCreationMode: this.isCreationMode,
            completedSubSteps: this.completedSubSteps,
            currentSubStep: this.currentSubStep,
            saveFlag: this.saveFlag,
            game: this.game,
            isManualBracket: this.isManualBracket,
            hasSavedBracket: this.hasSavedBracket,
            campaign: this.campaign
          }
        },
        {
          name: GameCreationTabs.GAME_RULES,
          title: this.$t('Gamification_GameRules'),
          component: GameStepGameRules,
          completed: this.checkIsTabCompleted(GameCreationTabs.GAME_RULES),
          bindProps: {
            isReadOnly: this.isReadOnly,
            isCreationMode: this.isCreationMode,
            completedSubSteps: this.completedSubSteps,
            currentSubStep: this.currentSubStep,
            saveFlag: this.saveFlag,
            gameCampaignId: this.getGameCampaignId
          },
          isDisabled: this.checkIsTabDisabled(GameCreationTabs.SET_UP)
        },
        {
          name: GameCreationTabs.CONTENT_ASSETS,
          component: GameStepContentAssets,
          title: this.$t('Gamification_ContentAssets'),
          completed: this.checkIsTabCompleted(GameCreationTabs.CONTENT_ASSETS),
          isDisabled: this.checkIsTabDisabled(GameCreationTabs.GAME_RULES),
          bindProps: {
            completedSubSteps: this.completedSubSteps,
            currentSubStep: this.currentSubStep,
            isReadOnly: this.isReadOnly,
            isCreationMode: this.isCreationMode,
            saveFlag: this.saveFlag,
            gameCampaignId: this.getGameCampaignId
          }
        },
        {
          name: GameCreationTabs.PRIZES,
          component: GameStepPrizes,
          title: this.$t('Gamification_Prizes'),
          completed: this.checkIsTabCompleted(GameCreationTabs.PRIZES),
          model: this.model,
          bindProps: {
            gameCampaignId: this.getGameCampaignId,
            isCreationMode: this.isCreationMode,
            isReadOnly: this.isReadOnly,
            saveFlag: this.saveFlag
          },
          isDisabled: this.checkIsTabDisabled(GameCreationTabs.CONTENT_ASSETS)
        },
        {
          name: GameCreationTabs.PREVIEW_LAUNCH,
          title: this.$t('Gamification_GameStepPreview_PreviewLaunch'),
          component: GameStepPreview,
          completed: this.checkIsTabCompleted(GameCreationTabs.PREVIEW_LAUNCH),
          isDisabled: this.checkIsTabDisabled(GameCreationTabs.PRIZES),
          bindProps: {
            isCreationMode: this.isCreationMode,
            isReadOnly: this.isReadOnly,
            saveFlag: this.saveFlag,
            gameCampaignId: this.getGameCampaignId
          }
        }
      ]

      if (this.isCampaignLive) {
        steps.push({
          name: GameCreationTabs.ANALYTICS,
          title: this.$t('Gamification_GameOverview'),
          component: GameStepAnalytics,
          completed: true,
          bindProps: {
            gameCampaignId: this.getGameCampaignId,
            isReadOnly: this.isReadOnly
          }
        })
      }

      return steps
    },
    rules() {
      return {}
    },
    fetchGameEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `games/${this.$route.params.id}`,
        params: { expand: 'gameRegions' },
        handler: data => {
          this.game = data
        }
      })
    },
    fetchBasicCampaignInfo() {
      const campaignId = this.getGameCampaignId
      if (!campaignId) {
        return
      }

      return projectEndpoint({
        method: 'get',
        url: `gamify/${this.$route.params.id}/get-game-campaign/${campaignId}`,
        handler: data => {
          this.campaign = data['data']

          if (
            this.campaign &&
            this.campaign.setup_step &&
            this.isCreationMode
          ) {
            this.lastFinishedStep = this.getStepById(this.campaign.setup_step)
            this.manageSteps(this.lastFinishedStep, true)
          }
        }
      })
    },
    fetchEndpoint() {
      return [this.fetchGameEndpoint, this.fetchBasicCampaignInfo]
    },
    breadcrumbs() {
      let breadcrumbs = [
        {
          label: this.$t('Gamification_GamesHub_Page_Title'),
          route: projectRoute({
            name: 'GamesHub'
          })
        },
        {
          label: this.game.name,
          route: projectRoute({
            name: 'GameCampaignsList',
            params: { id: this.game.id }
          })
        }
      ]
      if (this.$route.name === 'GameCreateCampaignPage') {
        breadcrumbs.push({
          label: this.$t('Gamification_CampaignCreateCampaign')
        })
      }
      if (
        (this.$route.name === 'GameEditCampaignPage' ||
          this.$route.name === 'GameViewCampaignPage') &&
        this.campaign &&
        this.campaign.name
      ) {
        breadcrumbs.push({
          label: this.campaign.name
        })
      }
      return breadcrumbs
    },
    isReadOnly() {
      return this.$route.name === 'GameViewCampaignPage'
    },
    isCreationMode() {
      const routeName = this.$route.name

      return (
        routeName === 'GameCreateCampaignPage' ||
        (routeName === 'GameEditCampaignPage' &&
          this.campaign &&
          this.campaign.state === 'draft')
      )
    },
    isCampaignLive() {
      return this.campaign && this.campaign.state === 'live'
    },
    getGameCampaignId() {
      return this.$route.params.campaignId
        ? parseInt(this.$route.params.campaignId)
        : parseInt(this.gameCampaignId)
    },
    getFrontendUrl() {
      return (this.campaign && this.campaign.frontend_url) || null
    },

    pageTag() {
      const state = this.campaign.state ? this.campaign.state.toLowerCase() : ''

      if (state === 'live') {
        return {
          label: this.$t('Gamification_CampaignList_Status_Live'),
          type: 'info',
          class: 'status-tag'
        }
      }

      return null
    },

    pageTitle() {
      const routeName = this.$route.name

      if (
        routeName === 'GameEditCampaignPage' ||
        routeName === 'GameViewCampaignPage'
      ) {
        return this.campaign.name || ''
      }

      return this.$t('Gamification_Game_CreateGameCampaignBtn')
    },

    isManualBracket() {
      return !!(this.campaign && this.campaign.manual_bracket)
    },

    hasSavedBracket() {
      return !!(this.campaign && this.campaign.has_bracket)
    }
  },

  mounted() {
    EventBus.$on('saveGameCampaignId', event => {
      this.gameCampaignId = event
    })
    EventBus.$on('updateStep', this.updateStep)
    EventBus.$on('updateCampaign', this.updateCampaign)
  },
  beforeDestroy() {
    EventBus.$off('saveGameCampaignId')
    EventBus.$off('updateStep', this.updateStep)
  },
  async created() {
    await this.$store.dispatch('fetchTimezones')
  },
  methods: {
    discardChangesConfirm() {
      return customConfirm(
        this.$t('Gamification_LeavingWithoutSaving'),
        this.$t('Gamification_LeavingWithoutSavingDescription'),
        this.$t('Gamification_Confirm_Button_Leave')
      )
    },

    confirmLeave() {
      if (!this.hasChanges || (this.saveFlag === true && this.isCreationMode)) {
        return Promise.resolve()
      }

      return this.discardChangesConfirm()
    },

    backToCampaignsList() {
      this.$router.push(projectRoute({ name: 'GameCampaignsList' }))
    },
    checkIsButtonDisabled(payload) {
      this.isSaveButtonDisabled = payload
    },
    manageSteps(lastFinishedStep, isOnMounted) {
      const allSteps = Object.values(GameCreationTabSubStepsMap).flat()
      const subSteps = this.filterSteps(allSteps)

      const lastIndex = subSteps.indexOf(lastFinishedStep)

      if (!this.completedSubSteps.includes(lastFinishedStep)) {
        this.completedSubSteps =
          lastIndex !== -1 ? subSteps.slice(0, lastIndex + 1) : []
      }

      this.currentSubStep =
        lastIndex !== -1 && lastIndex < subSteps.length - 1
          ? subSteps[lastIndex + 1]
          : undefined

      if (isOnMounted) {
        const tab = this.getTabBySubStep(this.currentSubStep)

        if (!tab) return

        const route = projectRoute({
          name: this.$route.name,
          hash: `#${tab}`,
          query: { ...this.$route.query }
        })

        this.$router.replace(route)
      }
    },
    checkIsTabCompleted(name) {
      if (this.isCampaignLive) {
        return true
      }

      const allSubSteps = GameCreationTabSubStepsMap[name] || []
      const applicableSubSteps = this.filterSteps(allSubSteps)

      if (applicableSubSteps.length) {
        return applicableSubSteps.every(subStep =>
          this.completedSubSteps.includes(subStep)
        )
      }
      return false
    },
    checkIsTabDisabled(previousTab) {
      if (!this.isCreationMode) {
        return false
      }

      const allSubSteps = GameCreationTabSubStepsMap[previousTab]
      const previousTabSubSteps = this.filterSteps(allSubSteps)

      return !previousTabSubSteps.every(step =>
        this.completedSubSteps.includes(step)
      )
    },
    checkChanges(payload) {
      this.hasChanges = payload
    },
    toggleSaveFlag() {
      this.saveFlag = !this.saveFlag
    },
    async copyGameUrl() {
      const url = this.campaign && this.campaign.frontend_url
      if (!url) return

      try {
        await navigator.clipboard.writeText(url)
        this.$message &&
          this.$message.success(this.$t('Gamification_CopyLink_Success'))
      } catch (e) {
        this.$message &&
          this.$message.error(this.$t('Gamification_CopyLink_Error'))
        console.error(e)
      }
    },
    getTabBySubStep(subStep) {
      const entry = Object.entries(
        GameCreationTabSubStepsMap
      ).find(([, subSteps]) => subSteps.includes(subStep))

      return entry ? entry[0] : undefined
    },
    updateStep(step) {
      const campaignId = this.getGameCampaignId

      if (!campaignId || !this.isCreationMode) {
        return
      }

      if (!this.completedSubSteps.includes(step)) {
        const subStepId = GameCreationSubStepId[step]

        const stepEndpoint = projectEndpoint({
          method: 'post',
          url: `gamify/${this.$route.params.id}/campaign/${campaignId}/step`,
          data: {
            setup_step: subStepId
          }
        })

        RestApi(stepEndpoint).catch(error => {
          console.error(error)
        })
      }

      this.manageSteps(step, false)
    },
    getStepById(id) {
      return Object.keys(GameCreationSubStepId).find(
        key => GameCreationSubStepId[key] === id
      )
    },
    updateCampaign(payload) {
      Object.keys(payload).forEach(key => {
        this.$set(this.campaign, key, payload[key])
      })
    },
    filterSteps(steps) {
      let filteredSteps = [...steps]

      if (!this.isBracketPredictorGame) {
        filteredSteps = filteredSteps.filter(
          step => !BracketPredictorGameSpecificSteps.includes(step)
        )
      }

      if (!this.isManualBracket) {
        filteredSteps = filteredSteps.filter(
          step => !ManualBracketPredictorGameSpecificSteps.includes(step)
        )
      }

      return filteredSteps
    }
  }
}
</script>

<style lang="scss" scoped>
.action-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-campaign-sub-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-left: 22px;

  .game-url {
    display: inline-flex;
    align-items: center;
    margin-bottom: 15px;
    background-color: #fff;
    border-radius: 6px;
    height: 34px;
    width: 70%;
    padding: 0 25px;
    font-weight: 400;
    font-size: 16px;
    color: #1e40af;
    overflow: hidden;
  }

  .game-url__link {
    display: inline-block;
    flex: 1 1 auto;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: inherit;
    text-decoration: underline;
    text-decoration-thickness: 1px;
  }

  .game-url__copy-btn {
    flex: 0 0 auto;
    margin-left: 8px;
    font-size: 17px;
  }
}

>>> .status-tag.el-tag {
  background: #b4dbf4 !important;
  color: #176ca1 !important;
  border: none !important;
  padding: 1px 4px !important;
  border-radius: 4px !important;
  font-size: 11px !important;
  line-height: 16px !important;
  font-weight: 600 !important;
  margin-top: 4px !important;
}
</style>



// WEBPACK FOOTER //
// src/modules/gamification/pages/GameCampaignPage.vue